// @ts-strict-ignore
import { ArticleUsageInfoPropertyDef } from '@article/constants/article-usage-info-property-def';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { ArticleConnectionService } from '@3d-scene/model3d/article-connection.service';
import { Injectable } from '@angular/core';
import { NumberUtils } from '@shared/util/number-utils';
import { CarrierBlockUtils } from '@project/components/article/util/carrier-block-utils';
import { CarrierBlockVO, ProjectVO } from '@project/model/vo/project.vo';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ProjectModel } from '@project/model/project.model';
import { InsertArticleHelper } from '../helper/insert-article.helper';
import { ProjectContentPresenter } from '@shared/presenter/project-content.presenter';
import { CarrierBlockManager } from '@project/components/article/manager/carrier-block.manager';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { ArticleUsageUtils } from '../util/article-usage-utils';
import { LocaleUtils } from '@shared/util/locale-utils';
import { StringUtils } from '@shared/util/string-utils';
import { ArticleUtils } from '../util/article-utils';
import { escape } from 'lodash';
import { CustomArticleUtils } from '../util/custom-article-utils';
import { ArticleNumberMappingVO } from '../model/vo/article-number-mapping.vo';
import { ArticleNumberMappingUtils } from '../util/article-number-mapping-utils';
import { UserModel } from '@user/model/user.model';
import { ArticleContainerUtils } from '@3d-scene/model3d/util/article-container-utils';
import { DateFormatter } from '@shared/date-formatter';
import { CompositeArticleInfoUtils } from '../util/composite-article-info-utils';
import { LoadProjectInfoService } from '@project/service/load-project-info.service';

type ToolTipTextContext = {
  article: ArticleVO,
  usage: ArticleUsageVO,
  container: ArticleContainer,
  articleToProcess: ArticleVO,
  containersToProcess: ArticleContainer[],
  targetContainerToProcess: ArticleContainer,
}

@Injectable({providedIn: 'root'})
export class ArticleToolTipTextUtils {
  private static LINE_SEPARATOR: string = '<br>';

  public constructor(
    private compositeArticleInfoUtils: CompositeArticleInfoUtils,
    private containerService: ContainerService,
    private articleConnectionService: ArticleConnectionService,
    private localeResourceManager: LocaleResourceManager,
    private userModel: UserModel,
    private projectModel: ProjectModel,
    private mountingRailHelper: MountingRailHelper,
    private presenter: ProjectContentPresenter,
    private carrierBlockManager: CarrierBlockManager,
    private loadProjectInfoService: LoadProjectInfoService,
  ) {}

  public createTechnicalInfoTooltipText(article: ArticleVO): string {
    let text = this.localeResourceManager.getValueByKey('sidebar.search.technical_info');
    if (ArticleUtils.isArticleOutdated(article)) {
      const currentDate: Date = new Date();
      if (!article.lastCall || article.lastCall < currentDate) {
        text += ArticleToolTipTextUtils.LINE_SEPARATOR +
          '<b>' +
          this.localeResourceManager.getValueByKey('articleToolTipManager.tooltip.cannotBeOrdered') +
          '</b>';
      } else {
        const dateString: string = DateFormatter.formatShortDate(article.lastCall);
        text += ArticleToolTipTextUtils.LINE_SEPARATOR +
          '<b>' +
          this.localeResourceManager.getValueByKey('articleToolTipManager.tooltip.cannotBeOrderedFrom', [dateString]) +
          '</b>';
      }
    }
    return text;
  }

  public createToolTipText(article: ArticleVO, usage: ArticleUsageVO): string {
    const context: ToolTipTextContext = this.createContext(article, usage);
    if (!context) {
      return null;
    }

    let text: string = '';
    text += this.getArticleNumberAndVersionFragment(context);
    text += this.getDescriptionFragment(context);
    text += this.getLocationFragment(context);
    text += this.getOpenSideFragment(context);
    text += this.getOutdatedFragment(context);

    return text;
  }

  public getArticleTreeDataTip(article: ArticleVO, usage: ArticleUsageVO): string {
    if (this.loadProjectInfoService.isLoading || !article || !usage) {
      return null;
    }
    return this.createToolTipText(article, usage);
  }

  private createContext(article: ArticleVO, usage: ArticleUsageVO): ToolTipTextContext | null {
    if (!article || !usage) {
      return null;
    }

    const container: ArticleContainer = this.containerService.findArticleContainerInGlobal(usage.id);
    if (!container) {
      return null;
    }

    let articleToProcess: ArticleVO;
    const containersToProcess: ArticleContainer[] = [];

    if (this.compositeArticleInfoUtils.isCompositePartArticle(container)) {
      articleToProcess = this.compositeArticleInfoUtils.getCompositeArticleFromContainer(container);
      for (const partContainer of this.containerService.getCompositePartArticles(container)) {
        if (this.articleConnectionService.isArticleConnectedToMountingRail(partContainer)) {
          containersToProcess.push(partContainer);
        }
      }

      if (containersToProcess.length === 0) {
        containersToProcess.push(container);
      }
    } else {
        articleToProcess = article;
        containersToProcess.push(container);
    }
    return <ToolTipTextContext> {
      article: article,
      usage: usage,
      container: container,
      articleToProcess: articleToProcess,
      containersToProcess: containersToProcess,
      targetContainerToProcess: containersToProcess[0],
    };
  }

  private getArticleNumberAndVersionFragment(context: ToolTipTextContext): string {
    let text: string = '';

    if (ArticleUtils.isCustomArticle(context.articleToProcess)) {
      text += escape(CustomArticleUtils.getCustomArticleNumber(context.usage));
    } else {
      text += context.articleToProcess.articleNo;
      const mapping: ArticleNumberMappingVO = ArticleNumberMappingUtils.findMappingByArticleNumber(
        context.articleToProcess.articleNo,
        this.userModel.articleNumberMappings
      );
      if (mapping) {
        text += ' [' + escape(mapping.customArticleNumber) + ']';
      }
      text += ' (Version ' + context.articleToProcess.version + ')';
    }
    return text;
  }

  private getDescriptionFragment(context: ToolTipTextContext): string {
    const displayName: string = LocaleUtils.getValueByLocaleChain(context.articleToProcess.displayName, [this.localeResourceManager.currentLanguage.langKey]);
    return StringUtils.isEmpty(displayName)
      ? ''
      : ArticleToolTipTextUtils.LINE_SEPARATOR + displayName;
  }

  private getLocationFragment(context: ToolTipTextContext): string {
    const positionOnRail = NumberUtils.getRoundedValueInMillimeters(context.container.position.x);
    const positionOnRailString = ' (' + positionOnRail + 'mm)';

    if (this.hasCarrierBlocks(this.projectModel.currentProject)) {
      // in case articles are split into multiple blocks, the position inside specific block should be shown
      const carrierBlock: CarrierBlockVO = this.findCarrierBlock(this.projectModel.currentProject, context.targetContainerToProcess);
      if (carrierBlock) {
        const articlePositionInBlock: number = this.calculateArticlePositionInBlock(context.containersToProcess, carrierBlock);
        if (NumberUtils.isNumber(articlePositionInBlock)) {
          let structurePositionName = this.getStructurePostionName(context, carrierBlock);
          return ArticleToolTipTextUtils.LINE_SEPARATOR +
            this.localeResourceManager.getValueByKey('main.menu.label.position_in_block', [articlePositionInBlock.toString(), structurePositionName ? `(${structurePositionName})` : ""]) +
            positionOnRailString;
        }
      }
    } else if (this.presenter) {
      const articlePosition: string = this.presenter.getPositionLabelByArticleUsageId(context.targetContainerToProcess.articleGUID);
      if (articlePosition.length > 0) {
        return ArticleToolTipTextUtils.LINE_SEPARATOR +
          this.localeResourceManager.getValueByKey('main.menu.label.position') +
          ': ' +
          articlePosition +
          positionOnRailString;
      }
    }
    return '';
  }

  /** extracts the structurePositionName from the articlesUsages CAE_ITEM_STRUCTURE or falls back to the carrierblocks name */
  private getStructurePostionName(context: ToolTipTextContext, carrierBlock: CarrierBlockVO): string {
    const bmkStructure = ArticleUsageUtils.getArticleUsageInfoPropertyStringValue(context.targetContainerToProcess.articleUsage, ArticleUsageInfoPropertyDef.CAE_ITEM_STRUCTURE);
    let structurePositionName = bmkStructure?.split(/-(.*)/s)[1];
    return structurePositionName ? '-' + structurePositionName : carrierBlock.name;
  }

  private hasCarrierBlocks(project: ProjectVO): boolean {
    const carrierBlockCount = project.carrierBlocks?.length ?? 0;
    return carrierBlockCount > 0;
  }

  private findCarrierBlock(project: ProjectVO, articleContainer: ArticleContainer): CarrierBlockVO | null {
    const carrierBlockGuid: string = CarrierBlockUtils.getCarrierBlockGuid(articleContainer.articleUsage);
    if (carrierBlockGuid) {
      const carrierBlock: CarrierBlockVO = CarrierBlockUtils.getCarrierBlockInProjectByGuid(project, carrierBlockGuid);
      if (carrierBlock?.containsArticleGuid(articleContainer.articleGUID)) {
        return carrierBlock;
      }
    }
    return null;
  }

  private getOpenSideFragment(context: ToolTipTextContext): string {
    // open side determined only for single article
    if (context.containersToProcess.length === 1) {
      const isRightSideOpen: boolean = ArticleContainerUtils.isRightSideOpen(context.targetContainerToProcess);
      const isLeftSideOpen: boolean = ArticleContainerUtils.isLeftSideOpen(context.targetContainerToProcess);
      if (isRightSideOpen && isLeftSideOpen) {
        return ArticleToolTipTextUtils.LINE_SEPARATOR +
          this.localeResourceManager.getValueByKey('articleToolTipManager.tooltip.both_side_open');
      } else if (isRightSideOpen) {
        return ArticleToolTipTextUtils.LINE_SEPARATOR +
          this.localeResourceManager.getValueByKey('articleToolTipManager.tooltip.right_side_open');
      } else if (isLeftSideOpen) {
        return ArticleToolTipTextUtils.LINE_SEPARATOR +
          this.localeResourceManager.getValueByKey('articleToolTipManager.tooltip.left_side_open');
      }
    }
    return '';
  }

  private getOutdatedFragment(context: ToolTipTextContext): string {
    if (ArticleUtils.isArticleOutdated(context.articleToProcess)) {
      const currentDate: Date = new Date();
      if (!context.article.lastCall || context.article.lastCall < currentDate) {
        return ArticleToolTipTextUtils.LINE_SEPARATOR +
          '<b>' +
          this.localeResourceManager.getValueByKey('articleToolTipManager.tooltip.cannotBeOrdered') +
          '</b>';
      } else {
        const dateString: string = DateFormatter.formatShortDate(context.article.lastCall);
        return ArticleToolTipTextUtils.LINE_SEPARATOR +
          '<b>' +
          this.localeResourceManager.getValueByKey('articleToolTipManager.tooltip.cannotBeOrderedFrom', [dateString]) +
          '</b>';
      }
    }
    return '';
  }

  private calculateArticlePositionInBlock(containersOnRail: ArticleContainer[], carrierBlock: CarrierBlockVO): number {
    if (!containersOnRail || containersOnRail.length === 0 || !carrierBlock) {
      return NaN;
    }
    const insertArticleHelper: InsertArticleHelper = this.mountingRailHelper.getInsertArticleHelper();
    if (!insertArticleHelper) {
      return NaN;
    }
    containersOnRail = insertArticleHelper.sortArticles(containersOnRail);
    const targetContainerOnRail: ArticleContainer = containersOnRail[0];
    if (!carrierBlock.guidsSorted) {
      this.carrierBlockManager.sortArticlesInCarrierBlock(carrierBlock, insertArticleHelper);
    }
    const targetContainerPosition: number = carrierBlock.articleGuids.indexOf(targetContainerOnRail.articleGUID);
    if (targetContainerPosition < 0) {
      return NaN;
    }
    const guidsBeforeTarget: string[] = carrierBlock.articleGuids.slice(0, targetContainerPosition);
    const amountOfArticlesBeforeProcessed: number = this.calculateAmountOfArticles(
      this.containerService.findArticleContainersInGlobalByGuids(guidsBeforeTarget)
    );
    return amountOfArticlesBeforeProcessed + 1;
  }

  private calculateAmountOfArticles(containers: ArticleContainer[]): number {
    if (!containers || containers.length === 0) {
      return 0;
    }
    let result: number = 0;
    const processedCompositionIds: string[] = [];
    let currentCompositionId: string = '';
    for (const container of containers) {
      currentCompositionId = ArticleUsageUtils.getCompositeArticleUsageIdFromUsage(container.articleUsage);
      if (!currentCompositionId || processedCompositionIds.indexOf(currentCompositionId) === -1) {
        result++;
        if (currentCompositionId) {
          processedCompositionIds.push(currentCompositionId);
        }
      }
    }
    return result;
  }
}
